"use client";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useCookies } from "react-cookie";
import ClientOAuth2, { Token } from "client-oauth2";
import { handleCheck } from "./UtilityFunctions";
import { useParams, useRouter } from "next/navigation";
import { queueRequest } from "./offline-queue";
import {
  tokenRefreshManager,
  TOKEN_STATUS,
  needsRefresh,
  getTokenStatus,
  isAuthError,
  isNetworkError,
  isRetryableError,
  delay,
} from "./token-refresh-manager";
import { getDeviceInfo } from "./device-info";
import { usePanelWorkspace } from "./PanelWorkspace";

const token = new Token(
  new ClientOAuth2({
    clientId: process.env.NEXT_PUBLIC_REACT_APP_CLIENT_ID,
    clientSecret: process.env.NEXT_PUBLIC_REACT_APP_CLIENT_SECRET,
    accessTokenUri:
      process.env.NEXT_PUBLIC_REACT_APP_MIDDLEWARE_URL + "/user/login",
    authorizationUri:
      process.env.NEXT_PUBLIC_REACT_APP_MIDDLEWARE_URL + "/user/login",
    redirectUri: "https://surveyanalytica.com/user/login/callback",
    scopes: ["notifications", "gist"],
  }),
  {
    username: "",
    password: "",
    confirm_password: "",
    error: "",
    loggedIn: "false",
  },
);

const AuthContext = createContext({});
export const AuthProvider = ({ children }) => {
  const [cookie, setCookie, removeCookie] = useCookies(["user"]);
  const router = useRouter();
  const params = useParams();
  const lang = params?.lang;
  const { workspace } = usePanelWorkspace();
  const [user, setUser] = useState(undefined);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tokenStatus, setTokenStatus] = useState(TOKEN_STATUS.VALID);
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);

  // Warm /login ONCE, in an effect.
  //
  // This used to run in the render body (`if (typeof window !== 'undefined')
  // router.prefetch('/login')`), alongside a `usePathname()` whose value was never read.
  // AuthProvider is the top of the tree, so that pairing was the worst possible one: the
  // unused hook re-rendered the provider on every navigation, and each of those renders
  // scheduled another prefetch task — a render-phase side effect, one per render, forever.
  // With the root layout dynamic (it awaits headers()), each task can reach the network,
  // and on a custom domain every one of those requests re-runs the edge middleware.
  useEffect(() => {
    router.prefetch("/login");
  }, [router]);

  // Subscribe to token status changes from the refresh manager
  useEffect(() => {
    const unsubscribe = tokenRefreshManager.subscribeToStatus(setTokenStatus);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setIsLoading(true);

    // Preview mode: token + org arrive via URL params (iframe from portal editor).
    // Initialize auth from them so all components use useAuth() as normal.
    if (
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("preview") === "true"
    ) {
      const urlParams = new URLSearchParams(window.location.search);
      const previewToken = urlParams.get("token");
      const previewOrgId = urlParams.get("org");
      if (previewToken) {
        try {
          const claims = parseJwt(previewToken);
          const expiresAt = new Date(
            (claims.exp || Date.now() / 1000 + 3600) * 1000,
          );
          const previewUser = token.client.createToken({
            access_token: previewToken,
            token_type: "bearer",
            expires_in: expiresAt,
            localId: claims.uid || claims.sub || claims.user_id || "",
            loggedIn: true,
            rememberMe: false,
          });
          previewUser.expiresIn(expiresAt);
          previewUser.data.claim = claims;
          previewUser.data.expires_in = expiresAt;
          previewUser.data.loggedIn = true;
          previewUser.data.rememberMe = false;
          setUser(previewUser);
          if (previewOrgId) {
            setCurrentOrg({ id: previewOrgId });
          }
          setIsLoading(false);
          return;
        } catch {
          // fall through to normal flow if token is malformed
        }
      }
    }

    let user = typeof window !== "undefined" && localStorage.getItem("user");
    let currentOrg =
      typeof window !== "undefined" && localStorage.getItem("currentOrg");
    if (user === null && typeof window !== "undefined") {
      user = sessionStorage.getItem("user");
    }
    if (currentOrg === null && typeof window !== "undefined") {
      currentOrg = sessionStorage.getItem("currentOrg");
    }
    if (currentOrg !== null) {
      setCurrentOrg(JSON.parse(currentOrg));
    }
    if (user !== null) {
      let retrievedUser = getObject(user);
      retrievedUser.expiresIn(new Date(retrievedUser.data.expires_in));
      setUser(retrievedUser);
      setCookie("user", retrievedUser.data.localId, {
        path: "/",
        maxAge: 3600,
        domain: window.location.hostname,
      });

      // Eager token refresh: if token is expired/expiring, fire refresh immediately
      if (needsRefresh(retrievedUser.data.expires_in)) {
        tokenRefreshManager
          .executeRefresh(() => refreshUserWithRetry())
          .catch(() => {});
      }
    } else {
      setUser(null);
      removeCookie("user", {
        path: "/",
        maxAge: 3600,
      });
    }
    setIsLoading(false);
  }, []);

  const login = async (username, password, rememberMe, confirm) => {
    return await token.client.owner
      .getToken(username, password, { body: { confirm } })
      .then(async (response) => {
        // Portal mismatch — server rejects non-participants trying to use the panel.
        if (response.data.error === "WRONG_PORTAL") {
          return {
            wrongPortal: true,
            redirectUrl: response.data.redirectUrl,
          };
        }
        // Check for MFA required response
        if (response.data.mfaRequired === true) {
          return {
            mfaRequired: true,
            mfaEnabled: true,
            mfaPendingCredential: response.data.mfaPendingCredential,
            mfaInfo: response.data.mfaInfo,
            email: username,
          };
        }
        if (
          response.data.access_token === undefined ||
          response.data.access_token === ""
        ) {
          return "Please verify your account";
        } else {
          const expires_in = new Date();
          expires_in.setSeconds(
            expires_in.getSeconds() + Number(response.data.expires_in),
          );
          response.data.expires_in = expires_in;
          response.expiresIn(expires_in);
          response.data.claim = parseJwt(response.data.access_token);
          response.data.mfaEnabled = response.data.mfaEnabled || false;
          let loggedInUser = storeUser(response, true);

          typeof window !== "undefined" &&
            localStorage.setItem("sentToServer", "0");
          setCookie("user", loggedInUser.data.localId, {
            path: "/",
            maxAge: 3600,
            domain: window.location.hostname,
          });
          setOrg(response.data.orgs);
          try {
            localStorage.setItem("orgs", JSON.stringify(response.data.orgs));
          } catch {}
          if (response.data.orgs.length === 1) {
            setCurrentOrg(response.data.orgs.at(0));
          }
          setUser(loggedInUser);

          registerSession(loggedInUser, "PASSWORD");

          // Org requires MFA but user hasn't enrolled yet — signal setup needed
          if (response.data.mfaSetupRequired === true) {
            setMfaSetupRequired(true);
            return { mfaSetupRequired: true };
          }

          return true;
        }
      })
      .catch((error) => {
        return error.toString();
      });
  };

  const completeMfaLogin = async (mfaResponse) => {
    try {
      if (!mfaResponse.access_token) {
        return mfaResponse.error || "MFA verification failed";
      }

      const expires_in = new Date();
      expires_in.setSeconds(
        expires_in.getSeconds() + Number(mfaResponse.expires_in),
      );
      mfaResponse.expires_in = expires_in;
      mfaResponse.claim = parseJwt(mfaResponse.access_token);
      mfaResponse.mfaEnabled = true;
      let firebaseToken = token.client.createToken({ ...mfaResponse });
      firebaseToken.expiresIn(expires_in);
      let loggedInUser = storeUser(firebaseToken, true);

      typeof window !== "undefined" &&
        localStorage.setItem("sentToServer", "0");
      setCookie("user", loggedInUser.data.localId, {
        path: "/",
        maxAge: 3600,
        domain: window.location.hostname,
      });

      setOrg(mfaResponse.orgs);
      try {
        localStorage.setItem("orgs", JSON.stringify(mfaResponse.orgs));
      } catch {}
      if (mfaResponse.orgs.length === 1) {
        setCurrentOrg(mfaResponse.orgs.at(0));
      }
      setUser(loggedInUser);

      registerSession(loggedInUser, "PASSWORD", true);

      return true;
    } catch (error) {
      console.error("Complete MFA login error:", error);
      return error.toString();
    }
  };

  // Joins a full token response (same shape as the password grant: access_token,
  // expires_in, orgs, localId…) to the app session. Used by grants that complete
  // outside the password form, e.g. the email sign-in link on /auth/action.
  const completeTokenSession = async (tokenResponse, loginMethod) => {
    try {
      if (!tokenResponse?.access_token) {
        return tokenResponse?.error || "Sign-in failed";
      }

      const expires_in = new Date();
      expires_in.setSeconds(
        expires_in.getSeconds() + Number(tokenResponse.expires_in),
      );
      tokenResponse.expires_in = expires_in;
      tokenResponse.claim = parseJwt(tokenResponse.access_token);
      tokenResponse.mfaEnabled = tokenResponse.mfaEnabled || false;
      let firebaseToken = token.client.createToken({ ...tokenResponse });
      firebaseToken.expiresIn(expires_in);
      let loggedInUser = storeUser(firebaseToken, true);

      typeof window !== "undefined" &&
        localStorage.setItem("sentToServer", "0");
      setCookie("user", loggedInUser.data.localId, {
        path: "/",
        maxAge: 3600,
        domain: window.location.hostname,
      });

      setOrg(tokenResponse.orgs);
      try {
        localStorage.setItem("orgs", JSON.stringify(tokenResponse.orgs));
      } catch {}
      if (tokenResponse.orgs?.length === 1) {
        setCurrentOrg(tokenResponse.orgs.at(0));
      }
      setUser(loggedInUser);

      registerSession(loggedInUser, loginMethod || "EMAIL_LINK");

      return true;
    } catch (error) {
      console.error("Complete token session error:", error);
      return error.toString();
    }
  };

  const registerSession = async (
    loggedInUser,
    loginMethod,
    mfaVerified = false,
  ) => {
    try {
      const deviceInfo = getDeviceInfo();
      const sessionData = {
        deviceId: deviceInfo.deviceId,
        deviceType: deviceInfo.deviceType,
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        userAgent: deviceInfo.userAgent,
        loginMethod: loginMethod || "PASSWORD",
        mfaVerified: mfaVerified,
      };

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_REACT_APP_MIDDLEWARE_URL}/sessions/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${loggedInUser.data.access_token}`,
          },
          body: JSON.stringify(sessionData),
        },
      );

      if (response.ok) {
        const result = await response.json();
        if (typeof window !== "undefined" && result.sessionId) {
          localStorage.setItem("sa_session_id", result.sessionId);
        }
        return result;
      }
    } catch (error) {
      console.warn("Failed to register session:", error);
    }
    return null;
  };

  const loginWithExternalProviders = async (firebaseResponse, rememberMe) => {
    firebaseResponse.claim = parseJwt(
      firebaseResponse.access_token ?? firebaseResponse.oauthIdToken,
    );
    let firebaseToken = token.client.createToken({ ...firebaseResponse });
    if (
      firebaseToken.data.access_token === undefined ||
      firebaseToken.data.access_token === ""
    ) {
      return "Please verify your account";
    } else {
      const expires_in = new Date();
      expires_in.setSeconds(
        expires_in.getSeconds() + Number(firebaseToken.data.expires_in),
      );
      firebaseToken.data.expires_in = expires_in;
      firebaseToken.expiresIn(expires_in);
      let loggedInUser = storeUser(firebaseToken, rememberMe);

      typeof window !== "undefined" &&
        localStorage.setItem("sentToServer", "0");
      setCookie("user", loggedInUser.data.localId, {
        path: "/",
        maxAge: 3600,
        domain: window.location.hostname,
      });
      setOrg(firebaseToken.data?.orgs);
      try {
        localStorage.setItem("orgs", JSON.stringify(firebaseToken.data?.orgs));
      } catch {}
      if (firebaseToken.data?.orgs?.length === 1) {
        setCurrentOrg(firebaseToken.data.orgs.at(0));
      }
      setUser(loggedInUser);

      const loginMethod = firebaseResponse.providerId?.toUpperCase() || "OAUTH";
      registerSession(loggedInUser, loginMethod);

      return loggedInUser;
    }
  };

  const checkToken = useCallback(async () => {
    if (!user) return null;

    const expiresAt = user.data.expires_in;

    if (needsRefresh(expiresAt)) {
      if (tokenRefreshManager.isRefreshing) {
        return user;
      }
      const result = await tokenRefreshManager.executeRefresh(() =>
        refreshUserWithRetry(),
      );
      return result?.user || result;
    }

    return user;
  }, [user, currentOrg]);

  const authenticatedServerCall = useCallback(
    async (
      url,
      method,
      body,
      headers = true,
      customHeaders,
      responseType = "JSON",
    ) => {
      method = method === undefined ? "get" : method;

      const readPostPatterns = [
        "/search",
        "/list",
        "/filter",
        "/query",
        "/find",
        "/lookup",
        "/autocomplete",
        "/suggest",
        "/typeahead",
        "/paginate",
        "/export",
        "/download",
        "/preview",
        "/validate",
      ];

      const isReadPost =
        method.toLowerCase() === "post" &&
        readPostPatterns.some((pattern) => url.toLowerCase().includes(pattern));
      const isExplicitRead = customHeaders?.["X-Read-Operation"] === "true";
      const isWriteOperation =
        ["put", "patch", "delete"].includes(method.toLowerCase()) ||
        (method.toLowerCase() === "post" && !isReadPost && !isExplicitRead);
      const isOffline = typeof navigator !== "undefined" && !navigator.onLine;

      if (isOffline && isWriteOperation) {
        try {
          await queueRequest(url, method, body, customHeaders || {});
          return {
            success: true,
            queued: true,
            message: "Request queued for sync when online",
          };
        } catch (error) {
          throw new Error("Failed to queue offline request");
        }
      }

      return await checkToken().then((currentUser) =>
        fetch(
          url,
          signRequest(
            currentUser,
            method,
            body,
            headers,
            customHeaders,
            lang,
            currentOrg,
          ),
        )
          .then(async (response) => {
            if (response.status === 401) {
              const tokenExpired =
                !user?.data?.expires_in || needsRefresh(user.data.expires_in);
              if (tokenExpired) {
                try {
                  const refreshResult =
                    await (tokenRefreshManager.refreshPromise ||
                      tokenRefreshManager.executeRefresh(() =>
                        refreshUserWithRetry(),
                      ));
                  const refreshedUser = refreshResult?.user || refreshResult;
                  if (refreshedUser) {
                    const retryResponse = await fetch(
                      url,
                      signRequest(
                        refreshedUser,
                        method,
                        body,
                        headers,
                        customHeaders,
                        lang,
                        currentOrg,
                      ),
                    );
                    if (retryResponse.status === 401) {
                      logout();
                      return;
                    }
                    if (retryResponse.status === 402) {
                      return retryResponse
                        .clone()
                        .json()
                        .then((resp) => {
                          const dimension = resp.dimension || "users";
                          resp.error = resp.error || "Quota exhausted";
                          return resp;
                        });
                    }
                    try {
                      return responseType === "JSON"
                        ? retryResponse.clone()?.json()
                        : retryResponse;
                    } catch {
                      return retryResponse;
                    }
                  }
                } catch (refreshError) {
                  console.error("Token refresh failed on 401:", refreshError);
                }
              }
              logout();
              return;
            }
            if (response.status === 402) {
              return response
                .clone()
                .json()
                .then((resp) => {
                  resp.error = resp.error || "Quota exhausted";
                  return resp;
                })
                .catch(() => {
                  return { error: "Quota exhausted" };
                });
            }
            try {
              return responseType === "JSON"
                ? response.clone()?.json()
                : response;
            } catch (ex) {
              return response;
            }
          })
          .then((value) => {
            return value;
          })
          .catch((error) => {
            if (typeof navigator !== "undefined" && !navigator.onLine) {
              throw new Error(
                "You are currently offline. Please check your internet connection.",
              );
            }
            throw error;
          }),
      );
    },
    [checkToken],
  );

  const [org, setOrg] = useState(() => {
    if (typeof window === "undefined") return null;
    try {
      const stored = localStorage.getItem("orgs");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const mutateOrg = async (accessToken) => {
    const doFetch = (bearerToken) =>
      fetch(`${process.env.NEXT_PUBLIC_REACT_APP_MIDDLEWARE_URL}/org`, {
        method: "GET",
        mode: "cors",
        crossDomain: true,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "Accept-Language": lang,
          Authorization: `Bearer ${bearerToken}`,
          Org: currentOrg?.id ?? "",
        },
      });
    try {
      let response = await doFetch(accessToken);
      if (response.status === 401) {
        const tokenExpired =
          !user?.data?.expires_in || needsRefresh(user.data.expires_in);
        if (tokenExpired) {
          try {
            const refreshResult = await (tokenRefreshManager.refreshPromise ||
              tokenRefreshManager.executeRefresh(() => refreshUserWithRetry()));
            const refreshedUser = refreshResult?.user || refreshResult;
            if (refreshedUser) {
              response = await doFetch(refreshedUser.data.access_token);
              if (response.status === 401) {
                logout();
                return;
              }
            } else {
              logout();
              return;
            }
          } catch {
            logout();
            return;
          }
        } else {
          logout();
          return;
        }
      }
      const value = await response.clone().json();
      setOrg(value);
      try {
        localStorage.setItem("orgs", JSON.stringify(value));
      } catch {}
      if (value.length === 1) {
        setCurrentOrg(value.at(0));
      }
      return true;
    } catch (error) {
      console.log(
        `${process.env.NEXT_PUBLIC_REACT_APP_MIDDLEWARE_URL}/org`,
        error,
        "error",
      );
    }
  };
  useEffect(() => {
    if (currentOrg) {
      localStorage.setItem("currentOrg", JSON.stringify(currentOrg));
    }
  }, [currentOrg]);

  // On a custom domain the tenant is fixed to the org that owns the domain.
  // participant-ui never switches orgs, so pin org[] to that one org and force
  // it as the active org — overriding any stale selection carried in
  // localStorage (e.g. a different org last chosen in udaserve on this browser).
  // `workspace` is the panel-workspace cookie ({orgId,...}); its presence IS the
  // custom-domain signal (PanelWorkspace sets isCustomDomain = !!workspace).
  useEffect(() => {
    if (!org || !Array.isArray(org) || !workspace?.orgId) return;
    const domainOrg = org.find((o) => o.id === workspace.orgId) || org[0];
    if (!domainOrg) return;
    if (org.length !== 1 || org[0]?.id !== domainOrg.id) {
      setOrg([domainOrg]);
      try {
        localStorage.setItem("orgs", JSON.stringify([domainOrg]));
      } catch {}
    }
    if (currentOrg?.id !== domainOrg.id) {
      setCurrentOrg(domainOrg);
    }
  }, [org, workspace, currentOrg]);

  useEffect(() => {
    if (user && !org) {
      mutateOrg(user.data.access_token);
    }
  }, [user]);

  async function deleteAllIndexedDB() {
    if (!indexedDB.databases) {
      return;
    }
    try {
      const databases = await indexedDB.databases();
      const deletePromises = databases.map((db) =>
        indexedDB.deleteDatabase(db.name),
      );
      await Promise.all(deletePromises);
    } catch (error) {
      console.error("Error deleting databases:", error);
    }
  }

  const logout = () => {
    if (typeof window !== "undefined") {
      const sessionId = localStorage.getItem("sa_session_id");
      if (sessionId && user?.data?.access_token) {
        fetch(
          `${process.env.NEXT_PUBLIC_REACT_APP_MIDDLEWARE_URL}/sessions/${sessionId}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${user.data.access_token}` },
          },
        ).catch(() => {});
      }
      localStorage.removeItem("sa_session_id");
      localStorage.removeItem("sa_device_id");
      localStorage.removeItem("orgs");
    }

    typeof window !== "undefined"
      ? user?.data?.rememberMe
        ? window.localStorage.clear()
        : window.sessionStorage.clear()
      : "";

    deleteAllIndexedDB().then((resp) => {
      setUser(null);
      setOrg(null);
      setCurrentOrg(null);
      removeCookie("user", {
        path: "/",
        maxAge: 3600,
      });
    });
    // Read the URL lazily, INSIDE the handler — never with useSearchParams() at
    // provider level. AuthProvider sits inside the root <Suspense>, and that hook
    // there bails the ENTIRE app out of server rendering (every route ships an
    // empty shell). This runs on a click, so window is always defined.
    const isPreview =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("preview") === "true";
    if (!isPreview) {
      router.push("/");
    }
  };

  const refreshUserWithRetry = async (retryCount = 0) => {
    const MAX_RETRIES = 3;

    if (!user) {
      throw new Error("No user to refresh");
    }

    user.client = new ClientOAuth2({
      clientId: process.env.NEXT_PUBLIC_REACT_APP_CLIENT_ID,
      clientSecret: process.env.NEXT_PUBLIC_REACT_APP_CLIENT_SECRET,
      accessTokenUri:
        process.env.NEXT_PUBLIC_REACT_APP_MIDDLEWARE_URL + "/user/login",
      authorizationUri:
        process.env.NEXT_PUBLIC_REACT_APP_MIDDLEWARE_URL + "/user/login",
      redirectUri: "https://surveyanalytica.com/user/login/callback",
      scopes: ["notifications", "gist"],
    });

    try {
      const response = await user.refresh();

      if (response !== undefined && response.responseCode === 0) {
        logout();
        return null;
      }

      const expires_in = new Date();
      expires_in.setSeconds(
        expires_in.getSeconds() + Number(response.data.expires_in),
      );
      response.data.expires_in = expires_in;
      response.expiresIn(expires_in);

      let updatedOrg = null;
      if (response.data?.orgs) {
        setOrg(response.data.orgs);
        try {
          localStorage.setItem("orgs", JSON.stringify(response.data.orgs));
        } catch {}
        if (response.data.orgs?.length === 1) {
          updatedOrg = response.data.orgs.at(0);
          setCurrentOrg(updatedOrg);
        } else if (response.data.orgs?.length > 1) {
          updatedOrg =
            response.data.orgs.find((o) => o.id === currentOrg?.id) ||
            response.data.orgs.at(0);
          if (updatedOrg) {
            setCurrentOrg(updatedOrg);
          }
        }
      }

      response.data.claim = parseJwt(response.data.access_token);
      let loggedInUser = storeUser(response, true);
      setUser(loggedInUser);
      setCookie("user", loggedInUser.data.localId, {
        path: "/",
        maxAge: 3600,
        domain: window.location.hostname,
      });

      return {
        user: loggedInUser,
        org: updatedOrg,
        orgs: response.data.orgs,
        accountType: updatedOrg?.accountType,
      };
    } catch (error) {
      if (isNetworkError(error) && retryCount < MAX_RETRIES) {
        await delay(retryCount);
        return refreshUserWithRetry(retryCount + 1);
      }
      if (isRetryableError(error) && retryCount < MAX_RETRIES) {
        await delay(retryCount);
        return refreshUserWithRetry(retryCount + 1);
      }
      if (isAuthError(error)) {
        logout();
        return null;
      }
      throw error;
    }
  };

  const refreshUser = async () => {
    return tokenRefreshManager.executeRefresh(() => refreshUserWithRetry());
  };

  const memoizedValue = useMemo(
    () => ({
      isAuthenticated: !!user && !!currentOrg,
      user,
      isLoading,
      org,
      currentOrg,
      mutateOrg,
      setCurrentOrg,
      login,
      completeMfaLogin,
      completeTokenSession,
      authenticatedServerCall,
      loginWithExternalProviders,
      refreshUser,
      tokenStatus,
      isTokenRefreshing: tokenStatus === TOKEN_STATUS.REFRESHING,
      isTokenExpiringSoon: tokenStatus === TOKEN_STATUS.EXPIRING_SOON,
      isTokenValid: tokenStatus === TOKEN_STATUS.VALID,
      mfaEnabled: user?.data?.mfaEnabled || false,
      mfaSetupRequired,
      markMfaSetupComplete: () => setMfaSetupRequired(false),
      isValidUser: !!user && !!currentOrg,
      isOrgOwner: user && currentOrg?.role === "ROLE_OWNER",
      isOrgAdmin:
        user &&
        ["ROLE_ADMIN", "ROLE_OWNER"].some((role) => role === currentOrg?.role),
      isOrgBillingAdmin:
        user &&
        ["ROLE_ADMIN", "ROLE_OWNER", "ROLE_BILLING"].some(
          (role) => role === currentOrg?.role,
        ),
      isAgent:
        currentOrg &&
        user &&
        user.data.loggedIn &&
        !["ROLE_ADMIN", "ROLE_OWNER"].some(
          (role) => role === currentOrg?.role,
        ) &&
        handleCheck(user.data.claim, ["ROLE_AGENT"], currentOrg.accountType),
      isParticipant:
        currentOrg && user && user.data.accountType === "PARTICIPANT",
      isInstituteManager:
        currentOrg &&
        user &&
        user.data.loggedIn &&
        !["ROLE_ADMIN", "ROLE_OWNER"].some(
          (role) => role === currentOrg?.role,
        ) &&
        handleCheck(
          user.data.claim,
          ["ROLE_INSTITUTE_MANAGER", "ROLE_ADMIN", "ROLE_SUPER"],
          currentOrg.accountType,
        ),
      isSurveyor:
        currentOrg &&
        user &&
        user.data.loggedIn &&
        (["ROLE_ADMIN", "ROLE_OWNER"].some(
          (role) => role === currentOrg?.role,
        ) ||
          handleCheck(
            user.data.claim,
            ["ROLE_SURVEYOR", "ROLE_ADMIN", "ROLE_SUPER"],
            currentOrg.accountType,
          )),
      isAdmin:
        currentOrg &&
        user &&
        user.data.loggedIn &&
        (["ROLE_ADMIN", "ROLE_OWNER"].some(
          (role) => role === currentOrg?.role,
        ) ||
          handleCheck(
            user.data.claim,
            ["ROLE_ADMIN", "ROLE_SUPER"],
            currentOrg.accountType,
          )),
      isSuperUser:
        currentOrg &&
        user &&
        user.data.loggedIn &&
        handleCheck(
          user.data.claim,
          ["ROLE_SUPER"],
          currentOrg.accountType,
          false,
        ),
      isContentCreator:
        currentOrg &&
        user &&
        user.data.loggedIn &&
        !["ROLE_ADMIN", "ROLE_OWNER"].some(
          (role) => role === currentOrg?.role,
        ) &&
        handleCheck(
          user.data.claim,
          ["ROLE_SUPER", "ROLE_CONTENT_ADMIN"],
          currentOrg.accountType,
          false,
        ),
      logout,
    }),
    [user, currentOrg, org, tokenStatus, mfaSetupRequired],
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
};
export const useAuth = () => useContext(AuthContext);

const signRequest = (
  requestUser,
  method,
  body,
  headers,
  customHeaders,
  lang,
  currentOrg,
) => {
  const token = Buffer.from(
    `${process.env.NEXT_PUBLIC_REACT_APP_CLIENT_ID}:${process.env.NEXT_PUBLIC_REACT_APP_CLIENT_SECRET}`,
    "utf8",
  ).toString("base64");
  const sessionId =
    typeof window !== "undefined"
      ? localStorage.getItem("sa_session_id")
      : null;

  const baseHeaders = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "Accept-Language": lang,
    Org: currentOrg?.id ?? "",
    ...(sessionId && { "X-Session-Id": sessionId }),
  };
  return (
    requestUser?.sign({
      method: method,
      mode: "cors",
      crossDomain: true,
      headers: headers
        ? customHeaders
          ? {
              ...customHeaders,
              ...(sessionId && { "X-Session-Id": sessionId }),
            }
          : baseHeaders
        : {
            Org: currentOrg?.id ?? "",
            ...(sessionId && { "X-Session-Id": sessionId }),
          },
      body: body,
    }) ?? {
      method: method,
      mode: "cors",
      crossDomain: true,
      headers: headers
        ? customHeaders
          ? {
              ...customHeaders,
              Authorization: `Basic ${token}`,
              Org: currentOrg?.id ?? "",
              ...(sessionId && { "X-Session-Id": sessionId }),
            }
          : {
              ...baseHeaders,
              Authorization: `Basic ${token}`,
            }
        : "",
      body: body,
    }
  );
};
const storeUser = (user, rememberMe) => {
  user.data.rememberMe = rememberMe;
  user.data.loggedIn = true;
  const newVal = setObject(user);
  typeof window !== "undefined"
    ? rememberMe
      ? localStorage.setItem("user", newVal)
      : sessionStorage.setItem("user", newVal)
    : "";

  typeof window !== "undefined"
    ? localStorage.setItem("uid", user.data.localId)
    : "";

  return user;
};

const getObject = function (value) {
  let base64ToString = Buffer.from(value, "base64").toString();
  base64ToString = JSON.parse(base64ToString);
  return new Token(base64ToString.client, base64ToString.data);
};
const setObject = function (inboundState) {
  let seen = [];
  return Buffer.from(
    JSON.stringify(inboundState, function (key, val) {
      if (val != null && typeof val == "object") {
        if (seen.indexOf(val) >= 0) {
          return;
        }
        seen.push(val);
      }
      return val;
    }),
    "utf8",
  ).toString("base64");
};

function parseJwt(token) {
  let base64Url = token.split(".")[1];
  let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  let jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(""),
  );

  return JSON.parse(jsonPayload);
}
